import type { AttendanceItem } from "views/Attendance/types"
import type { RequestLeaveItem } from "views/RequestLeave/types"

import { AttendanceAction, RequestLeaveAction, RequestLeaveStatus } from "__generated__/graphql"
import { DateTime, Interval } from "luxon"
import { getDateRange, getDefaultWorkCycleDateRange } from "utils/dateRange"
import { ATTENDANCE_ACTION_GROUPS, ATTENDANCE_ACTION_PAIRS } from "views/Attendance/constants"

type AttendanceFilters = {
  showChat: boolean
  showCheckins: boolean
  showFullTime: boolean
  showOvertime: boolean
  showPartTime: boolean
}

export const getDefaultAttendanceDateRange = getDefaultWorkCycleDateRange
export { getDateRange }

export const getAttendanceOverview = (
  attendances: AttendanceItem[],
  leaves: RequestLeaveItem[],
  startDate: DateTime,
  endDate: DateTime,
) => {
  const attendancesByDate = new Map<string, AttendanceItem[]>()
  attendances.forEach((attendance) => {
    const records = attendancesByDate.get(attendance.date) ?? []
    records.push(attendance)
    attendancesByDate.set(attendance.date, records)
  })

  const leaveDays = new Set<string>()
  const requestEarlyDays = new Set<string>()
  const requestLateDays = new Set<string>()
  const remoteDays = new Set<string>()
  const dateRange = getDateRange(startDate, endDate)

  leaves.forEach((leave) => {
    if (leave.status === RequestLeaveStatus.Rejected) return

    const interval = Interval.fromDateTimes(
      DateTime.fromISO(leave.startDate).startOf("day"),
      DateTime.fromISO(leave.endDate).endOf("day"),
    )

    dateRange.forEach((date) => {
      if (!interval.contains(date)) return
      const key = date.toFormat("yyyy-MM-dd")

      if (leave.action === RequestLeaveAction.RequestLeave) leaveDays.add(key)
      if (leave.action === RequestLeaveAction.RequestLeaveEarly) requestEarlyDays.add(key)
      if (leave.action === RequestLeaveAction.RequestLate) requestLateDays.add(key)
      if (leave.action === RequestLeaveAction.RequestRemote) remoteDays.add(key)
    })
  })

  return dateRange.reduce(
    (summary, date) => {
      const dayAttendances = attendancesByDate.get(date.toFormat("yyyy-MM-dd")) ?? []
      summary.missingCheckoutCount += getMissingCheckoutCount(dayAttendances)
      summary.totalMinutes += calculateWorkingTime(dayAttendances)
      if (isCheckinLate(dayAttendances)) summary.lateCount += 1
      return summary
    },
    {
      lateCount: 0,
      leaveCount: leaveDays.size,
      missingCheckoutCount: 0,
      remoteCount: remoteDays.size,
      requestEarlyCount: requestEarlyDays.size,
      requestLateCount: requestLateDays.size,
      totalMinutes: 0,
    },
  )
}

export const filterAttendances = (attendances: AttendanceItem[], filters: AttendanceFilters) => {
  return attendances.filter((attendance) => {
    const isPartTime = Boolean(attendance.attendee.isPartTime)
    if (isPartTime ? !filters.showPartTime : !filters.showFullTime) return false
    if (ATTENDANCE_ACTION_GROUPS.checkInOut.has(attendance.action) && !filters.showCheckins) return false
    if (ATTENDANCE_ACTION_GROUPS.overtime.has(attendance.action) && !filters.showOvertime) return false
    if (ATTENDANCE_ACTION_GROUPS.chat.has(attendance.action) && !filters.showChat) return false
    return true
  })
}

export const getGroupedByUsers = (attendances: AttendanceItem[]) => {
  const groups = new Map<
    number,
    {
      attendances: AttendanceItem[]
      attendee: AttendanceItem["attendee"]
      id: number
    }
  >()

  attendances.forEach((attendance) => {
    const attendeeId = attendance.attendee.id
    const group = groups.get(attendeeId)

    if (group) {
      group.attendances.push(attendance)
      return
    }

    groups.set(attendeeId, {
      attendances: [attendance],
      attendee: attendance.attendee,
      id: attendeeId,
    })
  })

  return Array.from(groups.values())
}

export const getAttendanceActionTime = (attendance: AttendanceItem): DateTime => {
  const actionTime = DateTime.fromISO(String(attendance.actionTime))
  const date = DateTime.fromISO(attendance.date)

  if (!actionTime.isValid) return date
  if (!date.isValid) return actionTime

  return date.set({
    hour: actionTime.hour,
    millisecond: actionTime.millisecond,
    minute: actionTime.minute,
    second: actionTime.second,
  })
}

export const sortAttendancesByActionTime = (attendances: AttendanceItem[]) =>
  [...attendances].sort((a, b) => getAttendanceActionTime(a).toMillis() - getAttendanceActionTime(b).toMillis())

export const isCheckInAction = (action: AttendanceAction) => action.startsWith("CHECK_IN")
export const isCheckOutAction = (action: AttendanceAction) => action.startsWith("CHECK_OUT")

export const getFirstCheckIn = (attendances: AttendanceItem[]) => {
  const item = sortAttendancesByActionTime(attendances).find((attendance) => isCheckInAction(attendance.action))
  return item ? getAttendanceActionTime(item) : null
}

export const getLastCheckOut = (attendances: AttendanceItem[]) => {
  const item = sortAttendancesByActionTime(attendances)
    .reverse()
    .find((attendance) => isCheckOutAction(attendance.action))
  return item ? getAttendanceActionTime(item) : null
}

export const isCheckinLate = (attendances: AttendanceItem[]) => {
  const checkIn = sortAttendancesByActionTime(attendances).find(
    (attendance) => attendance.action === AttendanceAction.CheckIn,
  )
  const actionTime = checkIn ? getAttendanceActionTime(checkIn) : null
  if (!actionTime || actionTime.weekday >= 6) return false

  const startOfLate = actionTime.set({ hour: 9, minute: 44 })
  const endOfLate = actionTime.set({ hour: 13, minute: 0 })
  return actionTime > startOfLate && actionTime < endOfLate
}

export const hasLateArrivalRequest = (leaves: RequestLeaveItem[]) =>
  leaves.some((leave) => leave.action === RequestLeaveAction.RequestLate)

export const getMissingCheckoutCount = (attendances: AttendanceItem[]) => {
  const sortedAttendances = sortAttendancesByActionTime(attendances)

  return ATTENDANCE_ACTION_PAIRS.reduce((total, [checkInAction, checkOutAction]) => {
    const hasOpenSession = sortedAttendances.reduce((isOpen, attendance) => {
      if (attendance.action === checkInAction) return true
      if (attendance.action === checkOutAction) return false
      return isOpen
    }, false)

    return hasOpenSession ? total + 1 : total
  }, 0)
}

export const calculateWorkingTime = (attendances: AttendanceItem[]): number => {
  const sortedAttendances = sortAttendancesByActionTime(attendances)

  return ATTENDANCE_ACTION_PAIRS.reduce((totalMinutes, [checkInAction, checkOutAction]) => {
    let checkInTime: DateTime | null = null

    sortedAttendances.forEach((attendance) => {
      if (attendance.action === checkInAction && !checkInTime) {
        checkInTime = getAttendanceActionTime(attendance)
        return
      }

      if (attendance.action !== checkOutAction || !checkInTime) return

      let checkOutTime = getAttendanceActionTime(attendance)
      if (checkOutTime < checkInTime) checkOutTime = checkOutTime.plus({ day: 1 })

      totalMinutes += Math.max(0, checkOutTime.diff(checkInTime, "minute").minutes)
      checkInTime = null
    })

    return totalMinutes
  }, 0)
}
